import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import {
  ShieldCheck, ShieldOff, KeyRound, Mail, Smartphone, Loader2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Self-contained security card for the Settings page.
 * Handles enable email OTP / setup TOTP / disable / switch method, talking
 * directly to /api/security/2fa/* via the AuthContext axios instance.
 */
export const SecuritySection = () => {
  const { user, api, refreshUser } = useAuth();
  const [busy, setBusy] = useState(null); // 'enable_email' | 'setup_totp' | 'enable_totp' | 'disable' | 'switch_email'
  const [totp, setTotp] = useState(null); // { secret, qr_data_url } during enrollment
  const [totpCode, setTotpCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const enabled = !!user?.twofa_enabled;
  const method = user?.twofa_method;

  const enableEmail = async () => {
    setBusy('enable_email');
    try {
      await api.post('/security/2fa/enable-email');
      await refreshUser();
      toast.success('Email 2FA enabled — you’ll receive a code on next sign-in.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not enable email 2FA');
    } finally {
      setBusy(null);
    }
  };

  const switchToEmail = async () => {
    setBusy('switch_email');
    try {
      await api.post('/security/2fa/method', { method: 'email' });
      await refreshUser();
      toast.success('Switched to email 2FA.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not switch method');
    } finally {
      setBusy(null);
    }
  };

  const startTotpSetup = async () => {
    setBusy('setup_totp');
    try {
      const res = await api.post('/security/2fa/setup-totp');
      setTotp(res.data);
      setTotpCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start authenticator setup');
    } finally {
      setBusy(null);
    }
  };

  const enableTotp = async () => {
    if (totpCode.trim().length < 6) return;
    setBusy('enable_totp');
    try {
      await api.post('/security/2fa/enable-totp', { code: totpCode.trim() });
      setTotp(null);
      setTotpCode('');
      await refreshUser();
      toast.success('Authenticator app enabled. Save your secret somewhere safe.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code — try again');
    } finally {
      setBusy(null);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    if (!disablePassword) return;
    setBusy('disable');
    try {
      await api.post('/security/2fa/disable', { password: disablePassword });
      await refreshUser();
      setDisablePassword('');
      setShowDisable(false);
      toast.success('Two-factor authentication disabled.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not disable 2FA');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card className="border-zinc-200" data-testid="security-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-indigo-600" />
          Two-factor authentication
        </CardTitle>
        <CardDescription>
          Add an extra step at sign-in to keep your account safe.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status */}
        <div className="flex items-center justify-between p-3 rounded-md bg-zinc-50 border border-zinc-200">
          <div className="flex items-center gap-3">
            {enabled ? (
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            ) : (
              <ShieldOff className="w-5 h-5 text-zinc-400" />
            )}
            <div>
              <p className="text-sm font-medium text-zinc-900">
                {enabled ? '2FA is on' : '2FA is off'}
              </p>
              <p className="text-xs text-zinc-500">
                {enabled
                  ? `Method: ${method === 'totp' ? 'Authenticator app' : 'Email code'}`
                  : 'Recommended for any account that publishes to live social platforms.'}
              </p>
            </div>
          </div>
        </div>

        {!user?.email_verified && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Verify your email first before turning on 2FA.</span>
          </div>
        )}

        {!enabled && user?.email_verified && (
          <>
            <Separator />
            <div className="grid sm:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="justify-start h-auto py-3"
                onClick={enableEmail}
                disabled={!!busy}
                data-testid="enable-email-2fa-btn"
              >
                {busy === 'enable_email' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Mail className="w-4 h-4 mr-2 text-indigo-600" />
                )}
                <div className="text-left">
                  <div className="text-sm font-medium">Email code</div>
                  <div className="text-[11px] text-zinc-500">6-digit code via email</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="justify-start h-auto py-3"
                onClick={startTotpSetup}
                disabled={!!busy}
                data-testid="setup-totp-btn"
              >
                {busy === 'setup_totp' ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Smartphone className="w-4 h-4 mr-2 text-indigo-600" />
                )}
                <div className="text-left">
                  <div className="text-sm font-medium">Authenticator app</div>
                  <div className="text-[11px] text-zinc-500">Google Authenticator, 1Password…</div>
                </div>
              </Button>
            </div>
          </>
        )}

        {/* Mid-flow TOTP enrollment */}
        {totp && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="totp-enrollment">
            <div className="flex items-start gap-3">
              <img
                src={totp.qr_data_url}
                alt="Scan with authenticator"
                className="w-32 h-32 rounded-md bg-white p-1 border border-zinc-200"
              />
              <div className="text-xs text-zinc-700 space-y-1">
                <p className="font-medium">Scan this QR code in your authenticator app.</p>
                <p>Or enter this secret manually:</p>
                <code className="block bg-white border border-zinc-200 rounded px-2 py-1 font-mono text-[11px] break-all">
                  {totp.secret}
                </code>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="totp-code">Enter the 6-digit code from your app</Label>
              <Input
                id="totp-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, ''))}
                className="font-mono tracking-[0.4em]"
                data-testid="totp-confirm-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={enableTotp}
                disabled={busy === 'enable_totp' || totpCode.length < 6}
                data-testid="confirm-totp-btn"
              >
                {busy === 'enable_totp' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Verify and enable
              </Button>
              <Button variant="ghost" onClick={() => { setTotp(null); setTotpCode(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {enabled && (
          <>
            <Separator />
            <div className="flex flex-col sm:flex-row gap-2">
              {method === 'totp' ? (
                <Button
                  variant="outline"
                  onClick={switchToEmail}
                  disabled={!!busy}
                  data-testid="switch-to-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Switch to email codes
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={startTotpSetup}
                  disabled={!!busy}
                  data-testid="switch-to-totp-btn"
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Switch to authenticator app
                </Button>
              )}
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => setShowDisable((v) => !v)}
                data-testid="show-disable-2fa-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Disable 2FA
              </Button>
            </div>
            {showDisable && (
              <form onSubmit={disable} className="rounded-md border border-red-200 bg-red-50/40 p-4 space-y-3" data-testid="disable-2fa-form">
                <Label htmlFor="disable-pwd" className="text-sm text-red-900">
                  Confirm with your password
                </Label>
                <Input
                  id="disable-pwd"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  required
                  data-testid="disable-2fa-password"
                />
                <Button
                  type="submit"
                  variant="outline"
                  className="text-red-700 border-red-300 hover:bg-red-100"
                  disabled={busy === 'disable'}
                  data-testid="confirm-disable-2fa-btn"
                >
                  {busy === 'disable' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Turn off 2FA
                </Button>
              </form>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SecuritySection;
